/* LeaveWizardDialog — iter101.84
 *
 * Cancel-confirmation modal shown when a doctor is about to leave a
 * case wizard mid-flow. iter101.84 rewrite: previously the dialog had
 * a single "leave & save as draft" outcome, which meant every abandoned
 * case became a draft — clogging up the drafts list with garbage stubs.
 * The doctor asked for the correct flow: two explicit exits.
 *
 *   ┌─────────────────────────────────────────────┐
 *   │  ● Leave the wizard?                        │
 *   │  Pick what happens to this case.            │
 *   │                                             │
 *   │  ┌───────────────┐  ┌──────────────────┐    │
 *   │  │ 💾 SAVE AS    │  │ 🗑  DISCARD     │    │
 *   │  │    DRAFT      │  │     CASE        │    │
 *   │  │ resumable     │  │  needs a reason │    │
 *   │  └───────────────┘  └──────────────────┘    │
 *   │                                             │
 *   │  [ Continue Case Creation ]                 │
 *   └─────────────────────────────────────────────┘
 *
 * Design DNA matches CaseSubmittedDialog + ResumeDraftDialog (same
 * teal hero, animated ring, sparkle scatter, Continue button style)
 * so the three lifecycle moments of the wizard feel like a family.
 *
 * State machine (all inside this dialog — parent gets a single
 * callback per outcome):
 *   • state="choose"   → picker card
 *   • state="discard"  → reason input + Delete confirmation button
 *
 * Contract:
 *   <LeaveWizardDialog
 *       open={open}
 *       caseCode="IVQ-CRN-1"
 *       onStay={() => setOpen(false)}                   // stay in wizard
 *       onSaveDraft={() => { setOpen(false); navigate("/doctor"); }}
 *       onDiscard={async (reason) => {                  // permanent delete
 *           await api.put(`/cases/${id}/cancel`, { reason });
 *           setOpen(false);
 *           navigate("/doctor");
 *       }}
 *   />
 */
import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    BookmarkSimple,
    Trash,
    ArrowRight,
    ArrowLeft,
    Sparkle,
    FloppyDisk,
    Warning,
    ClockCounterClockwise,
    ArrowUUpLeft,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const HERO_TONES = {
    choose: {
        bg:   "bg-gradient-to-br from-teal-100/70 via-teal-50/40 to-transparent",
        icon: "bg-teal-500 text-white shadow-[0_8px_28px_rgba(20,184,166,0.35)]",
        ring: "border-teal-500/40",
        eyebrowBg: "bg-teal-100 text-teal-800",
        sparkle: "bg-teal-400",
    },
    discard: {
        bg:   "bg-gradient-to-br from-rose-100/70 via-rose-50/40 to-transparent",
        icon: "bg-rose-500 text-white shadow-[0_8px_28px_rgba(244,63,94,0.35)]",
        ring: "border-rose-500/40",
        eyebrowBg: "bg-rose-100 text-rose-800",
        sparkle: "bg-rose-400",
    },
};

const DEFAULT_REASONS = [
    "Duplicate case — created by mistake",
    "Wrong patient selected",
    "Patient no longer proceeding",
    "Switched to a different plan",
    "Testing / demo case",
];

export default function LeaveWizardDialog({
    open,
    caseCode = "",
    onStay,
    onSaveDraft,
    onDiscard,
}) {
    const [state, setState] = React.useState("choose");    // "choose" | "discard"
    const [reason, setReason] = React.useState("");
    const [busy, setBusy] = React.useState(false);

    // Reset the dialog every time it re-opens so a doctor who bailed on
    // the reason step doesn't see stale state on their next attempt.
    React.useEffect(() => {
        if (open) {
            setState("choose");
            setReason("");
            setBusy(false);
        }
    }, [open]);

    const tone = HERO_TONES[state];

    const handleDiscardConfirm = async () => {
        if (!reason.trim() || busy) return;
        setBusy(true);
        try {
            await onDiscard?.(reason.trim());
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => { if (!o) onStay?.(); }}
        >
            <DialogContent
                className="max-w-md p-0 gap-0 border-0 shadow-2xl overflow-hidden"
                data-testid="leave-wizard-dialog"
            >
                {/* Hero */}
                <div className={`relative px-6 pt-6 pb-4 overflow-hidden transition-colors ${tone.bg}`}>
                    <Decorations sparkleClass={tone.sparkle} />

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={state}
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 240, damping: 16, delay: 0.06 }}
                            className={`relative mx-auto w-16 h-16 rounded-full grid place-items-center ${tone.icon}`}
                            data-testid="leave-wizard-icon"
                        >
                            <motion.span
                                aria-hidden
                                className={`absolute inset-[-5px] rounded-full border-2 ${tone.ring}`}
                                initial={{ rotate: 0, opacity: 0 }}
                                animate={{ rotate: 360, opacity: 1 }}
                                transition={{
                                    rotate: { duration: 14, repeat: Infinity, ease: "linear" },
                                    opacity: { duration: 0.4 },
                                }}
                            />
                            <motion.span
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.22, type: "spring", stiffness: 320 }}
                            >
                                {state === "discard"
                                    ? <Warning size={28} weight="fill" />
                                    : <BookmarkSimple size={28} weight="fill" />}
                            </motion.span>
                        </motion.div>
                    </AnimatePresence>

                    <div className="text-center mt-3.5">
                        <div
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] ${tone.eyebrowBg}`}
                            data-testid="leave-wizard-eyebrow"
                        >
                            <Sparkle size={10} weight="fill" />
                            {state === "discard" ? "Discard this case?" : "Leaving the wizard?"}
                        </div>
                        <h2
                            className="font-display text-xl font-bold tracking-tight mt-2 leading-tight"
                            data-testid="leave-wizard-title"
                        >
                            {state === "discard"
                                ? "This will delete the case for good"
                                : "What should happen to this case?"}
                        </h2>
                        {caseCode && (
                            <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white border border-border font-mono text-[11.5px] font-semibold text-foreground shadow-sm">
                                {caseCode}
                            </div>
                        )}
                    </div>
                </div>

                {/* Body */}
                <div className="bg-card">
                    {state === "choose" ? (
                        <ChoosePane
                            onSaveDraft={onSaveDraft}
                            onDiscardIntent={() => setState("discard")}
                        />
                    ) : (
                        <DiscardPane
                            reason={reason}
                            setReason={setReason}
                            busy={busy}
                            onBack={() => setState("choose")}
                            onConfirm={handleDiscardConfirm}
                        />
                    )}
                </div>

                {/* Footer — only rendered on the "choose" screen. On the
                    Discard screen, the pane has its own Back + Delete
                    button row for tighter visual grouping with the
                    reason input. */}
                {state === "choose" && (
                    <div className="px-6 pb-4 pt-2 bg-card flex items-center gap-2 flex-wrap border-t border-border/50">
                        <Button
                            onClick={onStay}
                            variant="outline"
                            size="sm"
                            className="w-full justify-center rounded-full h-9 gap-1.5"
                            data-testid="leave-wizard-stay-btn"
                        >
                            <ArrowUUpLeft size={12} weight="bold" />
                            Continue Case Creation
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}


/* ── Screen 1 — pick between Save-as-Draft or Discard. ────────────── */
function ChoosePane({ onSaveDraft, onDiscardIntent }) {
    return (
        <div className="px-6 pt-4 pb-2">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-3">
                Choose what happens next
            </div>
            <div className="space-y-2.5">
                {/* Save as Draft — recommended path */}
                <button
                    type="button"
                    onClick={onSaveDraft}
                    className="w-full text-left group rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50/60 via-emerald-50/20 to-transparent p-3.5 hover:border-emerald-400 hover:shadow-md transition-all"
                    data-testid="leave-wizard-save-draft-btn"
                >
                    <div className="flex items-start gap-3">
                        <span className="shrink-0 w-9 h-9 rounded-full bg-emerald-500 text-white grid place-items-center shadow-[0_4px_12px_rgba(16,185,129,0.35)]">
                            <FloppyDisk size={17} weight="fill" />
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                                <div className="text-[13px] font-bold text-emerald-900 leading-tight">
                                    Save as Draft
                                </div>
                                <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">
                                    Recommended
                                </span>
                            </div>
                            <div className="text-[11.5px] text-emerald-800/80 mt-0.5 leading-snug">
                                Everything you&apos;ve captured stays under <b>My Cases › Drafts</b>. Resume any time, on any device.
                            </div>
                            <div className="mt-2 inline-flex items-center gap-1 text-[10.5px] font-semibold text-emerald-700 group-hover:gap-1.5 transition-all">
                                Save &amp; go to Dashboard <ArrowRight size={11} weight="bold" />
                            </div>
                        </div>
                    </div>
                </button>

                {/* Discard — destructive path */}
                <button
                    type="button"
                    onClick={onDiscardIntent}
                    className="w-full text-left group rounded-2xl border border-rose-200 bg-gradient-to-br from-rose-50/60 via-rose-50/20 to-transparent p-3.5 hover:border-rose-400 hover:shadow-md transition-all"
                    data-testid="leave-wizard-discard-btn"
                >
                    <div className="flex items-start gap-3">
                        <span className="shrink-0 w-9 h-9 rounded-full bg-rose-500 text-white grid place-items-center shadow-[0_4px_12px_rgba(244,63,94,0.35)]">
                            <Trash size={16} weight="fill" />
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-bold text-rose-900 leading-tight">
                                Discard Case
                            </div>
                            <div className="text-[11.5px] text-rose-800/80 mt-0.5 leading-snug">
                                Permanently delete this case. A reason is required for your audit log.
                            </div>
                            <div className="mt-2 inline-flex items-center gap-1 text-[10.5px] font-semibold text-rose-700 group-hover:gap-1.5 transition-all">
                                Choose discard reason <ArrowRight size={11} weight="bold" />
                            </div>
                        </div>
                    </div>
                </button>
            </div>
        </div>
    );
}


/* ── Screen 2 — reason capture + confirm delete. ───────────────────── */
function DiscardPane({ reason, setReason, busy, onBack, onConfirm }) {
    const canConfirm = reason.trim().length > 0 && !busy;
    return (
        <div className="px-6 pt-4 pb-4 space-y-3">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                Why are you discarding?
            </div>
            <div className="flex flex-wrap gap-1.5">
                {DEFAULT_REASONS.map((r) => (
                    <button
                        key={r}
                        type="button"
                        onClick={() => setReason(r)}
                        className={`px-2 py-1 rounded-full text-[10.5px] font-medium ring-1 transition-colors ${
                            reason === r
                                ? "bg-rose-600 text-white ring-rose-600"
                                : "bg-white text-foreground/80 ring-border hover:ring-rose-400 hover:text-rose-700"
                        }`}
                        data-testid={`leave-wizard-reason-chip-${r.slice(0, 12).replace(/\s+/g, "-")}`}
                    >
                        {r}
                    </button>
                ))}
            </div>
            <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Or type a specific reason…"
                rows={2}
                className="text-[12.5px] resize-none"
                data-testid="leave-wizard-reason-input"
            />
            <div className="rounded-lg bg-rose-50/50 border border-rose-200/70 px-3 py-2 flex items-start gap-2">
                <ClockCounterClockwise size={13} weight="duotone" className="text-rose-700 mt-0.5 shrink-0" />
                <div className="text-[11px] text-rose-900/85 leading-snug">
                    Discarded cases are hidden from your active list but remain in the audit trail for 90 days. This action can&apos;t be undone from your side.
                </div>
            </div>
            <div className="pt-1 flex items-center gap-2">
                <Button
                    onClick={onBack}
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    className="rounded-full h-9 gap-1.5"
                    data-testid="leave-wizard-discard-back-btn"
                >
                    <ArrowLeft size={12} weight="bold" /> Back
                </Button>
                <Button
                    onClick={onConfirm}
                    disabled={!canConfirm}
                    size="sm"
                    className="ml-auto rounded-full h-9 text-white gap-1.5 bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300 disabled:hover:bg-rose-300"
                    data-testid="leave-wizard-discard-confirm-btn"
                >
                    <Trash size={12} weight="fill" />
                    {busy ? "Discarding…" : "Discard Case Permanently"}
                </Button>
            </div>
        </div>
    );
}


function Decorations({ sparkleClass = "bg-primary" }) {
    return (
        <>
            <span className={`absolute top-3 left-4 w-1.5 h-1.5 rounded-full opacity-30 animate-ping [animation-duration:2200ms] [animation-delay:200ms] ${sparkleClass}`} />
            <span className={`absolute top-7 right-7 w-1 h-1 rounded-full opacity-40 animate-ping [animation-duration:2400ms] [animation-delay:500ms] ${sparkleClass}`} />
            <span className={`absolute bottom-4 left-10 w-1 h-1 rounded-full opacity-40 animate-ping [animation-duration:2600ms] [animation-delay:800ms] ${sparkleClass}`} />
            <span className={`absolute bottom-7 right-10 w-1.5 h-1.5 rounded-full opacity-25 animate-ping [animation-duration:2000ms] [animation-delay:0ms] ${sparkleClass}`} />
        </>
    );
}
